import { generatePath, useNavigate } from 'react-router';
import { Card, Column, Row, TextField, Typography } from '@tempmee/react';
import { LocationListItem } from '@tempmee/react-blocks';
import { OutlineSearchIcon } from '@tempmee/react-icons';
import { CenteredSpinner } from '~/commonComponents/CenteredSpinner';
import { useAppSelector } from '~/hooks/useAppSelector';
import { getOfficeAvatarUrl } from '~/utils/OfficeUtils';
import { routes } from '~/routes';
import { AccountPageLayout } from '~/components/shared/AccountArea';
import { useLocationList } from '~/hooks/useLocationList';
import { OrganizationSection } from './components/OrganizationSection';
import { LocationDetailPage } from './LocationDetailPage';
import styles from './LocationsPage.module.scss';

/**
 * `/locations`: a single-location office lands on its own page; a multi-location
 * account gets the Organization section, then every location, the parent
 * included, with a search that filters as they type (Figma 7169-77967).
 */
export const LocationsPage = () => {
  const navigate = useNavigate();
  const signedInOfficeId = useAppSelector((state) => state.user.user?.id);
  const { isMultiLocation, locations, masterOfficeId, query, setQuery } = useLocationList();

  if (!isMultiLocation) {
    return signedInOfficeId ? (
      <LocationDetailPage officeId={signedInOfficeId} />
    ) : (
      <CenteredSpinner />
    );
  }

  return (
    <AccountPageLayout title="All Locations">
      <Row justify="center">
        <Column gap={4} className={styles.list}>
          <OrganizationSection />
          <Card className="rounded-lg p-4" data-cy="locations-list">
            <Column gap={2}>
              <TextField
                placeholder="Search"
                aria-label="Search locations"
                leadingIcon={<OutlineSearchIcon />}
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                data-cy="locations-search"
              />
              {locations.length === 0 ? (
                <Typography variant="body1" data-cy="locations-empty">
                  No locations match
                </Typography>
              ) : (
                <Column>
                  {locations.map((office) => (
                    <LocationListItem
                      key={office.id}
                      locationId={office.id}
                      name={office.office_name}
                      address={office.address?.[0]?.formatted_address}
                      avatarUrl={getOfficeAvatarUrl(office.profile_image)}
                      badge={office.id === masterOfficeId ? 'ADMIN' : undefined}
                      onLocationClick={(officeId) =>
                        navigate(generatePath(routes.locationDetail, { officeId }))
                      }
                      testID={`location-row-${office.id}`}
                    />
                  ))}
                </Column>
              )}
            </Column>
          </Card>
        </Column>
      </Row>
    </AccountPageLayout>
  );
};
